import { type DailyScheduleSlotEntity } from "@/modules/boxDashboard/modules/DailySchedule/entities/dailyScheduleSlotEntity"
import {
  createDailyScheduleSlotModel,
  type DailyScheduleSlotModel,
} from "@/modules/boxDashboard/modules/DailySchedule/models/dailyScheduleSlotModel"

export type DailyScheduleModel = {
  isEmpty: boolean
  totalCount: number
  hasMore: boolean
  allSlots: DailyScheduleSlotModel[]
  visibleSlots: DailyScheduleSlotModel[]
  visibleStartIndex: number
}

const MAX_VISIBLE_SLOTS = 6

export function createDailyScheduleModel(slots: DailyScheduleSlotEntity[] = []): DailyScheduleModel {
  // Sort slots chronologically (mobile behavior)
  const sortedSlots = [...slots].sort((a, b) => {
    if (a.slotStart === null || b.slotStart === null) return 0
    const startComparison = a.slotStart.localeCompare(b.slotStart)
    if (startComparison !== 0) return startComparison
    if (a.slotEnd === null || b.slotEnd === null) return 0
    const endComparison = a.slotEnd.localeCompare(b.slotEnd)
    return endComparison
  })

  // Transform entities to slot models
  const slotModels: DailyScheduleSlotModel[] = sortedSlots.map((slot) => createDailyScheduleSlotModel(slot))

  // Find the index of the first upcoming (non-past) slot
  const firstUpcomingIndex = slotModels.findIndex((slot) => !slot.isPast)

  // Calculate visible slots: prioritize upcoming, fill remaining with recent passed
  let visibleStartIndex: number
  let visibleSlots: DailyScheduleSlotModel[]

  if (firstUpcomingIndex === -1) {
    // All slots are past - show the last MAX_VISIBLE_SLOTS
    visibleStartIndex = Math.max(0, slotModels.length - MAX_VISIBLE_SLOTS)
    visibleSlots = slotModels.slice(visibleStartIndex)
  } else {
    const upcomingCount = slotModels.length - firstUpcomingIndex

    if (upcomingCount >= MAX_VISIBLE_SLOTS) {
      // More upcoming slots than we can show - show first MAX_VISIBLE_SLOTS upcoming
      visibleStartIndex = firstUpcomingIndex
      visibleSlots = slotModels.slice(firstUpcomingIndex, firstUpcomingIndex + MAX_VISIBLE_SLOTS)
    } else {
      // Fill remaining slots with recent passed ones
      const passedSlotsToShow = MAX_VISIBLE_SLOTS - upcomingCount
      visibleStartIndex = Math.max(0, firstUpcomingIndex - passedSlotsToShow)
      visibleSlots = slotModels.slice(visibleStartIndex, visibleStartIndex + MAX_VISIBLE_SLOTS)
    }
  }

  return {
    isEmpty: slots.length === 0,
    totalCount: slots.length,
    hasMore: slots.length > MAX_VISIBLE_SLOTS,
    allSlots: slotModels,
    visibleSlots,
    visibleStartIndex,
  }
}
